import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { FinanceStatCard } from '@/modules/finance/components/FinanceStatCard';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  Download,
  Landmark,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Link as LinkIcon } from
'lucide-react';

import { financeAPI } from '@/shared/services/api';

export const Banking = () => {const { t } = useTranslation("finance");
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [reconciliationStatus, setReconciliationStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [accountsData, txnData, reconData] = await Promise.all([
      financeAPI.getBankAccounts(),
      financeAPI.getBankTransactions(),
      financeAPI.getReconciliationStatus()]
      );

      setBankAccounts(accountsData);
      setTransactions(txnData);
      setReconciliationStatus(reconData);

      if (accountsData.length > 0 && !selectedAccount) {
        setSelectedAccount(accountsData[0]);
      }
    } catch (err) {
      toast.error('Failed to load banking data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const totalBalance = bankAccounts.reduce((sum, a) => sum + (a.current_balance || 0), 0);
  const unmatchedCount = transactions.filter((t) => !t.matched).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="finance-banking">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("bankingReconciliation", "Banking & Reconciliation")}</h1>
          <p className="text-muted-foreground mt-1">{t("bankAccountsAnd", "Bank accounts and transaction matching")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button variant="outline" className="gap-2" onClick={() => toast.info('Statement export started...')}>
            <Download className="h-4 w-4" />{t("exportStatement", "Export Statement")}

          </Button>
          <Button className="gap-2 bg-emerald-600 hover:bg-emerald-700" onClick={() => toast.info('Add Transaction modal placeholder')}>
            <Plus className="h-4 w-4" />{t("addTransaction", "Add Transaction")}

          </Button>
        </div>
      </div>

      {/* Total Balance */}
      <Card className="card-elevated bg-gradient-to-r from-emerald-50/50 to-transparent dark:from-emerald-950/20">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("totalCashBalance", "Total Cash Balance")}</p>
              <p className="text-4xl font-bold font-['Space_Grotesk'] text-emerald-600">
                ${totalBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </p>
              <p className="text-sm text-muted-foreground mt-1">{bankAccounts.length}{t("accounts", "accounts")}</p>
            </div>
            <div className="p-4 rounded-xl bg-emerald-100 dark:bg-emerald-900/50">
              <Landmark className="h-8 w-8 text-emerald-600" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bank Accounts */}
      <div className="grid gap-4 sm:grid-cols-3">
        {bankAccounts.map((account) =>
        <Card
          key={account.id}
          className={`card-elevated cursor-pointer transition-all ${selectedAccount?.id === account.id ? 'ring-2 ring-emerald-500' : ''}`
          }
          onClick={() => setSelectedAccount(account)}>
          
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-medium">{account.name}</p>
                  <p className="text-sm text-muted-foreground">{account.bank}</p>
                </div>
                <Badge variant="outline">{account.type}</Badge>
              </div>
              <p className="text-2xl font-bold font-['Space_Grotesk']">
                ${account.current_balance?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-muted-foreground mt-2">{t("lastReconciled", "Last reconciled:")}
              {account.last_reconciled}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Reconciliation Status */}
      <Card className="card-elevated">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">{t("reconciliationStatus", "Reconciliation Status -")}{selectedAccount?.name}</CardTitle>
            <Button className="gap-2 bg-emerald-600 hover:bg-emerald-700" onClick={() => toast.success('Reconciliation analysis started...')}>
              <RefreshCw className="h-4 w-4" />{t("startReconciliation", "Start Reconciliation")}

            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-4">
            <div className="p-4 rounded-lg bg-muted/30">
              <p className="text-sm text-muted-foreground">{t("bookBalance", "Book Balance")}</p>
              <p className="text-xl font-bold">${reconciliationStatus?.book_balance?.toLocaleString() || 0}</p>
            </div>
            <div className="p-4 rounded-lg bg-muted/30">
              <p className="text-sm text-muted-foreground">{t("bankBalance", "Bank Balance")}</p>
              <p className="text-xl font-bold">${reconciliationStatus?.bank_balance?.toLocaleString() || 0}</p>
            </div>
            <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30">
              <p className="text-sm text-muted-foreground">{t("difference", "Difference")}</p>
              <p className="text-xl font-bold text-amber-600">${reconciliationStatus?.difference?.toLocaleString() || 0}</p>
            </div>
            <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30">
              <p className="text-sm text-muted-foreground">{t("unmatchedItems", "Unmatched Items")}</p>
              <p className="text-xl font-bold text-blue-600">{unmatchedCount}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transactions */}
      <Card className="card-elevated">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-lg">{t("recentTransactions", "Recent Transactions")}</CardTitle>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search transactions..." className="pl-10" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("date", "Date")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("description", "Description")}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("amount", "Amount")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("status", "Status")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("matched", "Matched")}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((txn) =>
                <tr key={txn.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    <td className="py-4 px-4 text-sm">{txn.date}</td>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2">
                        {txn.amount > 0 ?
                      <ArrowDownRight className="h-4 w-4 text-emerald-600" /> :

                      <ArrowUpRight className="h-4 w-4 text-red-600" />
                      }
                        <span className="text-sm">{txn.description}</span>
                      </div>
                    </td>
                    <td className={`py-4 px-4 text-right font-mono font-medium ${txn.amount > 0 ? 'text-emerald-600' : 'text-red-600'}`
                  }>
                      {txn.amount > 0 ? '+' : ''}{txn.amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                    <td className="py-4 px-4">
                      <Badge className={txn.status === 'cleared' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                        {txn.status?.charAt(0).toUpperCase() + txn.status?.slice(1)}
                      </Badge>
                    </td>
                    <td className="py-4 px-4">
                      {txn.matched ?
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" /> :

                    <AlertCircle className="h-5 w-5 text-amber-500" />
                    }
                    </td>
                    <td className="py-4 px-4 text-right">
                      {!txn.matched &&
                    <Button size="sm" variant="outline" className="gap-1" onClick={() => toast.success('Transaction matched!')}>
                          <LinkIcon className="h-3.5 w-3.5" />{t("match", "Match")}

                    </Button>
                    }
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>);

};